import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostBinding, Input, Optional, Output, Attribute } from '@angular/core';
import { TranslatePipe } from 'pipes/translate.pipe';
import { FormState, FormValidationService } from 'services/form-validation.service';

@Component({
	selector: 'button:not([mat-icon-button]):not([mat-menu-item])',
	templateUrl: './button.component.html',
	styleUrls: ['./button.component.scss'],
	imports: [NgClass, NgTemplateOutlet, TranslatePipe]
})
export class ButtonComponent {

	@Input() label = '';
	@Input() labelSaved?: string;
	@Input() progression?: { processed: number, total: number };
	
	@Input() loader = true;
	@Input() working = false;

	public isLocalLink = true;
	private _href?: string;
	@Input()
	set href(value: string | undefined) {
		this._href = value;
		if (!value) return;
		this.ignoreValidation = true;
		this.isLocalLink = !value.match(/^https?\:/);
	}
	get href() {
		return this._href;
	}

	@Input() selected = false;

	@Input() asyncCallback?: () => Promise<any> | undefined;

	@Output('click') click = new EventEmitter();

	private _ignoreValidation = false;
	@Input() set ignoreValidation(value: boolean) {
		this._ignoreValidation = value;
		this.updateStateClass();
	}
	get ignoreValidation() {
		return this._ignoreValidation;
	}

	private _allowWhenPristine = false;
	@Input() set allowWhenPristine(value: boolean) {
		this._allowWhenPristine = value;
		this.updateStateClass();
	}

	private _badgeText?: string;
	@Input() set badge(value: string | undefined) {
		this._badgeText = value;
	}
	get badgeText() {
		return this._badgeText;
	}

	private _active = true;
	@Input()
	set active(value: boolean) {
		this._active = value;
		this.updateStateClass();
	}
	get active() {
		return this._active;
	}
	hasAddBadge = false;

	@Input('confirm') isConfirmButton?: string;
	@HostBinding('attr.type') get htmlButtonType() { return this.isConfirmButton !== undefined ? 'submit' : 'button'; }

	public stateClass = 'disabled';
	private state: FormState = FormState.Pristine;

	constructor(@Optional() private formValidation: FormValidationService, elementRef: ElementRef<HTMLElement>, @Attribute('add') addAttribute: string) {
		if (addAttribute !== null) {
			this._badgeText = '+';
			this.hasAddBadge = true;
		}
		if (formValidation != null) {
			formValidation.stateChanged.subscribe(state => {
				this.state = state;
				setTimeout(() => { this.updateStateClass(); });
			});
		}
		else this.ignoreValidation = true;

		// Bind event manually using DOM instead of HostListener, to ensure it executes first, and ImmediatePropagation can be prevented!
		elementRef.nativeElement.addEventListener('click', (event: Event) => this.parseClick(event));
	}

	private updateStateClass() {
		if (!this.active)
			this.stateClass = 'disabled';
		else if (this.ignoreValidation)
			this.stateClass = 'valid';
		else
			switch (this.state) {
				case FormState.Invalid:
					this.stateClass = 'invalid';
					break;
				case FormState.Valid:
					this.stateClass = 'valid';
					break;
				case FormState.Pristine:
				case FormState.Saved:
					this.stateClass = this._allowWhenPristine ? 'valid' : 'disabled';
					break;
			}
	}


	parseClick(event: Event): boolean {
		if (this.href) return true;
		if ((this.state === FormState.Pristine || this.state === FormState.Saved) && this._allowWhenPristine) this.formValidation?.validatePristineFields(); // If allowing pristine form, force it to validate all fields to prevent submitting invalid pristine fields
		if ((this.state !== FormState.Valid && !this.ignoreValidation && this.state !== FormState.Pristine && this.state !== FormState.Saved)
			|| !this.active
			|| this.working) {
			event.stopPropagation();
			event.stopImmediatePropagation();
			return false;
		}
		if (this.loader) this.working = true;

		if (this.asyncCallback) this.asyncCallback()?.then(() => this.succeeded()).catch(() => this.reset());

		return true;
	}

	showSaved(): boolean {
		if (!this.labelSaved) return false;
		return (this.state === FormState.Saved);
	}

	public succeeded(): void {
		this.working = false;
		this.progression = undefined;
		if (this.formValidation != null) this.formValidation.saved();
		else this.state = FormState.Saved;
	}

	public reset(): void {
		this.working = false;
		this.progression = undefined;
		if (this.formValidation != null) this.formValidation.reset();
		else this.state = FormState.Valid;
	}
	
}
